/**
 * LiveKit Provider Implementation.
 *
 * This provider uses LiveKit's VP8 video track approach
 * to transport animation data via RTCRtpScriptTransform.
 *
 * @packageDocumentation
 */

import { BaseProvider } from '../base/BaseProvider';
import type {
  RTCConnectionConfig,
  RTCPrepareConnectionConfig,
  LiveKitConnectionConfig,
} from '../../types';
import { isLiveKitConfig, isLiveKitPrepareConfig } from '../../types';
import type {
  AnimationTrackCallbacks,
  AudioTrackCallbacks,
} from '../../core/types';
import { VP8Extractor } from './VP8Extractor';
import { getInsertableStreamsMethod } from './utils';
import { logger } from '../../utils';
import type {
  LiveKitModule,
  LiveKitRoom,
  LiveKitLocalAudioTrack,
} from './types';

/**
 * Global registry for LiveKitProvider instances to receive early track events.
 * @internal
 */
const globalLiveKitProviderRegistry = new Set<LiveKitProvider>();

/**
 * Flag to ensure we only patch RTCPeerConnection once.
 * @internal
 */
let rtcPeerConnectionPatched = false;

/**
 * Patch RTCPeerConnection.prototype to intercept track events at the earliest possible moment.
 * This runs BEFORE any PeerConnection is created by LiveKit.
 * @internal
 */
function patchRTCPeerConnection(): void {
  if (rtcPeerConnectionPatched) return;
  if (typeof RTCPeerConnection === 'undefined') return;

  rtcPeerConnectionPatched = true;

  const originalAddEventListener = RTCPeerConnection.prototype.addEventListener;

  RTCPeerConnection.prototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (type === 'track') {
      const wrappedListener = function (this: RTCPeerConnection, event: Event) {
        const trackEvent = event as RTCTrackEvent;

        if (
          (trackEvent.track?.kind === 'audio' ||
            trackEvent.track?.kind === 'video') &&
          trackEvent.receiver
        ) {
          for (const provider of globalLiveKitProviderRegistry) {
            provider.handleEarlyTrack(trackEvent.receiver, trackEvent.track);
          }
        }

        if (typeof listener === 'function') {
          listener.call(this, event);
        } else {
          listener.handleEvent(event);
        }
      };

      return originalAddEventListener.call(
        this,
        type,
        wrappedListener as EventListener,
        options,
      );
    }

    return originalAddEventListener.call(this, type, listener, options);
  };

  // Also patch the ontrack setter
  const originalOnTrackDescriptor = Object.getOwnPropertyDescriptor(
    RTCPeerConnection.prototype,
    'ontrack',
  );
  if (originalOnTrackDescriptor) {
    Object.defineProperty(RTCPeerConnection.prototype, 'ontrack', {
      get: originalOnTrackDescriptor.get,
      set: function (
        handler: ((this: RTCPeerConnection, ev: RTCTrackEvent) => void) | null,
      ) {
        if (handler) {
          const wrappedHandler = function (
            this: RTCPeerConnection,
            event: RTCTrackEvent,
          ) {
            if (
              (event.track?.kind === 'audio' ||
                event.track?.kind === 'video') &&
              event.receiver
            ) {
              for (const provider of globalLiveKitProviderRegistry) {
                provider.handleEarlyTrack(event.receiver, event.track);
              }
            }
            handler.call(this, event);
          };
          originalOnTrackDescriptor.set?.call(this, wrappedHandler);
        } else {
          originalOnTrackDescriptor.set?.call(this, handler);
        }
      },
      configurable: true,
      enumerable: true,
    });
  }
}

// Apply the patch immediately when this module loads
patchRTCPeerConnection();

/**
 * LiveKit Provider.
 *
 * Implements RTCProvider interface for LiveKit platform.
 * Uses RTCRtpScriptTransform to extract animation data from VP8 video tracks.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer, LiveKitProvider } from '@spatialwalk/avatarkit-rtc';
 *
 * const provider = new LiveKitProvider();
 * const player = new AvatarPlayer(provider, renderer);
 *
 * await player.connect({
 *   url: 'wss://your-livekit-server.com',
 *   token: 'your-token',
 * });
 * ```
 */
export class LiveKitProvider extends BaseProvider {
  /** Provider name identifier */
  readonly name = 'livekit';

  /** @internal */
  private room: LiveKitRoom | null = null;
  /** @internal */
  private livekitSDK: LiveKitModule | null = null;

  // Animation track subscription
  /** @internal */
  private animationCallbacks: AnimationTrackCallbacks | null = null;
  /** @internal */
  private vp8Extractor: VP8Extractor | null = null;
  /** @internal */
  private vp8Extractors: Map<RTCRtpReceiver, VP8Extractor> = new Map();
  /** @internal */
  private transformedReceivers: Set<RTCRtpReceiver> = new Set();
  /** @internal */
  private receiverParticipantMap: Map<
    RTCRtpReceiver,
    { participant: unknown; trackName?: string }
  > = new Map();

  // Audio track subscription
  /** @internal */
  private audioCallbacks: AudioTrackCallbacks | null = null;
  /** @internal */
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  /** @internal */
  private awaitingAudioUnlock = false;
  /** @internal */
  private readonly audioUnlockEventNames: Array<keyof WindowEventMap> = [
    'pointerdown',
    'keydown',
    'touchstart',
  ];
  /** @internal */
  private readonly handleAudioUnlockGesture = (): void => {
    void this.ensureAudioPlaybackUnlocked('user-gesture');
  };

  // Microphone publishing
  /** @internal */
  private localAudioTrack: LiveKitLocalAudioTrack | null = null;
  /** @internal */
  private isMicrophoneEnabled = false;

  constructor() {
    super();
    // Register to receive early track events
    globalLiveKitProviderRegistry.add(this);
  }

  private createRoom(livekit: LiveKitModule): LiveKitRoom {
    const { Room } = livekit;
    return new Room({
      videoCaptureDefaults: undefined,
      audioCaptureDefaults: undefined,
    });
  }

  /**
   * Load LiveKit SDK dynamically.
   * @internal
   */
  private async loadSDK(): Promise<LiveKitModule> {
    if (this.livekitSDK) {
      return this.livekitSDK;
    }

    try {
      this.livekitSDK = await import('livekit-client');
      return this.livekitSDK;
    } catch {
      throw new Error(
        '❌ Failed to load livekit-client.\n' +
          'Please ensure it is installed: pnpm add livekit-client',
      );
    }
  }

  async prepareConnection(config: RTCPrepareConnectionConfig): Promise<void> {
    logger.info('LiveKit', 'prepareConnection() called with config:', {
      hasUrl: 'url' in config,
      hasToken: 'token' in config,
    });

    if (!isLiveKitPrepareConfig(config)) {
      throw new Error('LiveKitProvider requires url in prepareConnection config');
    }

    const livekit = await this.loadSDK();

    if (!this.room) {
      this.room = this.createRoom(livekit);
    }

    try {
      await this.room.prepareConnection(config.url, config.token);
      logger.info('LiveKit', 'prepareConnection() completed');
    } catch (error) {
      logger.warn('LiveKit', 'prepareConnection() failed (non-fatal):', error);
    }
  }

  /**
   * Map LiveKit connection state to string.
   * @internal
   */
  private mapLiveKitConnectionState(
    livekit: typeof import('livekit-client'),
    state: unknown,
  ): string {
    const { ConnectionState: LKConnectionState } = livekit;
    switch (state) {
      case LKConnectionState.Disconnected:
        return 'disconnected';
      case LKConnectionState.Connecting:
        return 'connecting';
      case LKConnectionState.Connected:
        return 'connected';
      case LKConnectionState.Reconnecting:
        return 'reconnecting';
      default:
        return 'failed';
    }
  }

  /**
   * Handle early track event from the global RTCPeerConnection patch.
   * This is called BEFORE LiveKit processes the track.
   * @internal
   */
  handleEarlyTrack(receiver: RTCRtpReceiver, track: MediaStreamTrack): void {
    if (!this.room) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const roomState = (this.room as any).state;
    if (roomState === 'disconnected' || roomState === 0) return;

    // Apply transform to video tracks - animation data is sent as VP8 video
    if (track.kind === 'video' && this.animationCallbacks) {
      this.applyAnimationReceiverTransform(receiver, track);
    }
  }

  /**
   * Find all RTCPeerConnection instances in the room
   * @internal
   */
  private findAllPeerConnections(root: unknown): RTCPeerConnection[] {
    if (typeof RTCPeerConnection === 'undefined') return [];
    if (!root || typeof root !== 'object') return [];

    const seen = new Set<unknown>();
    const queue: Array<{ v: unknown; d: number }> = [{ v: root, d: 0 }];
    const pcs: RTCPeerConnection[] = [];
    let steps = 0;

    while (queue.length && steps < 2000) {
      steps++;
      const { v, d } = queue.shift()!;
      if (!v || typeof v !== 'object') continue;
      if (seen.has(v)) continue;
      seen.add(v);

      if (v instanceof RTCPeerConnection) {
        pcs.push(v);
        continue;
      }
      if (d >= 6) continue;

      if (Array.isArray(v)) {
        for (const item of v) queue.push({ v: item, d: d + 1 });
        continue;
      }

      if (v instanceof Map) {
        for (const item of v.values()) queue.push({ v: item, d: d + 1 });
        continue;
      }

      if (v instanceof Set) {
        for (const item of v.values()) queue.push({ v: item, d: d + 1 });
        continue;
      }

      try {
        for (const key of Object.keys(v as object)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queue.push({ v: (v as any)[key], d: d + 1 });
        }
      } catch {
        // ignore
      }
    }

    return pcs;
  }

  /**
   * Find video receiver by track ID
   * @internal
   */
  private findVideoReceiverByTrackId(trackId?: string): RTCRtpReceiver | null {
    if (!trackId) return null;
    const pcs = this.findAllPeerConnections(this.room);
    for (const pc of pcs) {
      try {
        const receivers = pc.getReceivers();
        for (const r of receivers) {
          if (r.track?.kind === 'video' && r.track.id === trackId) return r;
        }
      } catch {
        // ignore
      }
    }
    // fallback: any video receiver
    for (const pc of pcs) {
      try {
        const r = pc.getReceivers().find((x) => x.track?.kind === 'video');
        if (r) return r;
      } catch {
        // ignore
      }
    }
    return null;
  }

  /**
   * Find audio receiver by track ID
   * @internal
   */
  private findAudioReceiverByTrackId(trackId?: string): RTCRtpReceiver | null {
    if (!trackId) return null;
    const pcs = this.findAllPeerConnections(this.room);
    for (const pc of pcs) {
      try {
        const receivers = pc.getReceivers();
        for (const r of receivers) {
          if (r.track?.kind === 'audio' && r.track.id === trackId) return r;
        }
      } catch {
        // ignore
      }
    }
    // fallback: any audio receiver
    for (const pc of pcs) {
      try {
        const r = pc.getReceivers().find((x) => x.track?.kind === 'audio');
        if (r) return r;
      } catch {
        // ignore
      }
    }
    return null;
  }

  /**
   * Apply animation receiver transform on a video receiver
   * @internal
   */
  private applyAnimationReceiverTransform(
    receiver: RTCRtpReceiver,
    mediaTrack: MediaStreamTrack,
  ): void {
    if (mediaTrack.kind !== 'video') return;
    if (this.transformedReceivers.has(receiver)) return;

    const method = getInsertableStreamsMethod();
    if (method !== 'scriptTransform') return;
    if (receiver.transform) return;
    if (!this.animationCallbacks) return;

    this.transformedReceivers.add(receiver);

    try {
      const extractor = new VP8Extractor();
      this.vp8Extractors.set(receiver, extractor);
      this.vp8Extractor = extractor;

      extractor.initialize(receiver, this.animationCallbacks).catch((err) => {
        logger.error('LiveKit', 'Failed to initialize VP8Extractor:', err);
        this.transformedReceivers.delete(receiver);
        this.vp8Extractors.delete(receiver);
      });
    } catch (e) {
      logger.error('LiveKit', 'Failed to apply transform:', e);
      this.transformedReceivers.delete(receiver);
      this.vp8Extractors.delete(receiver);
    }
  }

  /**
   * Add one-time user gesture listeners to recover from autoplay blocking.
   * @internal
   */
  private addAudioUnlockListeners(): void {
    if (typeof window === 'undefined' || this.awaitingAudioUnlock) {
      return;
    }

    this.awaitingAudioUnlock = true;
    for (const eventName of this.audioUnlockEventNames) {
      window.addEventListener(eventName, this.handleAudioUnlockGesture, {
        capture: true,
        passive: true,
      });
    }

    logger.warn(
      'LiveKit',
      'Audio playback blocked by autoplay policy, waiting for user interaction',
    );
  }

  /**
   * Remove user gesture listeners after playback is unlocked.
   * @internal
   */
  private removeAudioUnlockListeners(): void {
    if (typeof window === 'undefined' || !this.awaitingAudioUnlock) {
      return;
    }

    this.awaitingAudioUnlock = false;
    for (const eventName of this.audioUnlockEventNames) {
      window.removeEventListener(
        eventName,
        this.handleAudioUnlockGesture,
        true,
      );
    }
  }

  /**
   * Detect browser autoplay blocking errors from audio play/start calls.
   * @internal
   */
  private isAutoplayBlockedError(error: unknown): boolean {
    if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
      return error.name === 'NotAllowedError';
    }

    if (error instanceof Error) {
      return (
        error.name === 'NotAllowedError' ||
        /didn't interact|user didn't interact|NotAllowedError/i.test(
          error.message,
        )
      );
    }

    return false;
  }

  /**
   * Try to resume all attached remote audio elements.
   * @returns true if at least one element is still blocked by autoplay policy
   * @internal
   */
  private async resumeAttachedAudioElements(reason: string): Promise<boolean> {
    let hasAutoplayBlockedElement = false;

    for (const [participantIdentity, audioElement] of this.audioElements) {
      if (!audioElement.paused) {
        continue;
      }

      try {
        await audioElement.play();
      } catch (error) {
        if (this.isAutoplayBlockedError(error)) {
          hasAutoplayBlockedElement = true;
        }
        logger.warn(
          'LiveKit',
          `Failed to resume remote audio element (${reason}) for ${participantIdentity}:`,
          error,
        );
      }
    }

    return hasAutoplayBlockedElement;
  }

  /**
   * Ensure remote audio playback is unlocked and resumed.
   * @internal
   */
  private async ensureAudioPlaybackUnlocked(reason: string): Promise<void> {
    if (!this.room) {
      return;
    }

    const roomWithAudioControl = this.room as LiveKitRoom & {
      startAudio?: () => Promise<void>;
      canPlaybackAudio: boolean;
    };

    if (
      !roomWithAudioControl.canPlaybackAudio &&
      typeof roomWithAudioControl.startAudio === 'function'
    ) {
      try {
        await roomWithAudioControl.startAudio();
        logger.info('LiveKit', `Audio context resumed (${reason})`);
      } catch (error) {
        if (this.isAutoplayBlockedError(error)) {
          this.addAudioUnlockListeners();
        } else {
          logger.warn(
            'LiveKit',
            `Failed to call room.startAudio() (${reason}):`,
            error,
          );
        }
        return;
      }
    }

    const hasAutoplayBlockedElement =
      await this.resumeAttachedAudioElements(reason);
    if (roomWithAudioControl.canPlaybackAudio && !hasAutoplayBlockedElement) {
      this.removeAudioUnlockListeners();
    } else {
      this.addAudioUnlockListeners();
    }
  }

  async connect(config: RTCConnectionConfig): Promise<void> {
    logger.info('LiveKit', 'connect() called with config:', {
      hasUrl: 'url' in config,
      hasRoomName: 'roomName' in config,
      hasToken: 'token' in config,
      config,
    });

    if (!isLiveKitConfig(config)) {
      logger.error(
        'LiveKit',
        'Config validation failed - missing url or token',
      );
      throw new Error(
        'LiveKitProvider requires url and token in connection config',
      );
    }

    const livekitConfig: LiveKitConnectionConfig = config;
    logger.info(
      'LiveKit',
      'Config validated, connecting to:',
      livekitConfig.url,
    );

    this.removeAudioUnlockListeners();

    const livekit = await this.loadSDK();
    logger.info('LiveKit', 'SDK loaded successfully');
    if (!this.room) {
      this.room = this.createRoom(livekit);
    }

    this.setConnectionState('connecting');

    // Setup event listeners
    this.setupEventListeners(livekit);

    try {
      logger.info('LiveKit', 'Attempting to connect to room...');
      await this.room.connect(livekitConfig.url, livekitConfig.token);
      logger.info('LiveKit', 'Room connected, state:', this.room.state);
      logger.info('LiveKit', 'Room name:', this.room.name);
      logger.info(
        'LiveKit',
        'Local participant:',
        this.room.localParticipant?.identity,
      );
      logger.info(
        'LiveKit',
        'Remote participants:',
        this.room.remoteParticipants.size,
      );

      // Log existing participants and their tracks
      this.room.remoteParticipants.forEach((participant: any, sid: string) => {
        logger.info(
          'LiveKit',
          `Remote participant: ${participant.identity}, sid: ${sid}`,
        );
        participant.trackPublications.forEach((pub: any, trackSid: string) => {
          logger.info(
            'LiveKit',
            `  Track: ${pub.trackName}, kind: ${pub.kind}, subscribed: ${pub.isSubscribed}, sid: ${trackSid}`,
          );
        });
      });

      this.setConnectionState(
        this.mapLiveKitConnectionState(livekit, this.room.state),
      );
    } catch (error) {
      logger.error('LiveKit', 'Connection failed:', error);
      this.setConnectionState('failed');
      this.emit('error', error as Error);
      throw error;
    }
  }

  /**
   * Setup LiveKit room event listeners
   * @internal
   */
  private setupEventListeners(livekit: LiveKitModule): void {
    if (!this.room) return;

    const room = this.room;
    const { RoomEvent, Track } = livekit;

    // Connected
    room.on(RoomEvent.Connected, () => {
      this.setConnectionState('connected');
      this.emit('connected');

      const roomWithAudioControl = room as LiveKitRoom & {
        canPlaybackAudio: boolean;
      };
      if (!roomWithAudioControl.canPlaybackAudio) {
        this.addAudioUnlockListeners();
      }
    });

    // Participant connected
    room.on(RoomEvent.ParticipantConnected, (participant: any) => {
      logger.info('LiveKit', `Participant connected: ${participant.identity}`);
    });

    // Participant disconnected
    room.on(RoomEvent.ParticipantDisconnected, (participant: any) => {
      logger.warn(
        'LiveKit',
        `Participant disconnected: ${participant.identity}`,
      );
    });

    // Track published
    room.on(RoomEvent.TrackPublished, (publication: any, participant: any) => {
      logger.info('LiveKit', 'TrackPublished:', {
        trackName: publication.trackName,
        kind: publication.kind,
        participant: participant.identity,
      });
    });

    // Disconnected
    room.on(RoomEvent.Disconnected, () => {
      this.cleanup();
      this.setConnectionState('disconnected');
      this.emit('disconnected');
    });

    room.on(
      RoomEvent.AudioPlaybackStatusChanged,
      (canPlaybackAudio: boolean) => {
        logger.info(
          'LiveKit',
          `Audio playback status changed: ${canPlaybackAudio ? 'enabled' : 'blocked'}`,
        );

        if (canPlaybackAudio) {
          void this.ensureAudioPlaybackUnlocked(
            'audio-playback-status-changed',
          );
        } else {
          this.addAudioUnlockListeners();
        }
      },
    );

    // Track subscribed
    this.room.on(
      RoomEvent.TrackSubscribed,
      (track: any, publication: any, participant: any) => {
        logger.info('LiveKit', 'TrackSubscribed:', {
          kind: track.kind,
          trackName: publication.trackName,
          participant: participant.identity,
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mediaStreamTrack = (track as any).mediaStreamTrack as
          | MediaStreamTrack
          | undefined;
        const trackName = publication.trackName;

        if (track.kind === Track.Kind.Audio) {
          logger.info('LiveKit', 'Audio track received, attaching...');
          // Audio track
          const audioElement = track.attach() as HTMLAudioElement;
          audioElement.id = `audio-${participant.identity}`;
          audioElement.autoplay = true;
          audioElement.controls = false;
          audioElement.style.display = 'none';

          const previousAudioElement = this.audioElements.get(
            participant.identity,
          );
          if (previousAudioElement && previousAudioElement !== audioElement) {
            previousAudioElement.remove();
          }

          if (typeof document !== 'undefined') {
            document.body.appendChild(audioElement);
            logger.info('LiveKit', 'Audio element appended to body');
          }

          this.audioElements.set(participant.identity, audioElement);
          void this.ensureAudioPlaybackUnlocked('audio-track-subscribed');

          if (this.audioCallbacks) {
            this.audioCallbacks.onAudioReceived?.(participant);
          }
        } else if (track.kind === Track.Kind.Video) {
          logger.info(
            'LiveKit',
            'Video track received, setting up animation transform...',
          );
          // Video track - apply animation transform
          if (mediaStreamTrack) {
            const receiver = this.findVideoReceiverByTrackId(
              mediaStreamTrack.id,
            );
            if (receiver) {
              this.receiverParticipantMap.set(receiver, {
                participant,
                trackName,
              });

              // Apply transform if not already applied in handleEarlyTrack
              if (
                !receiver.transform ||
                !this.transformedReceivers.has(receiver)
              ) {
                this.applyAnimationReceiverTransform(
                  receiver,
                  mediaStreamTrack,
                );
              }
            }
          }

          // Attach to hidden video element to trigger data flow
          const videoElement = track.attach() as HTMLVideoElement;
          videoElement.id = `video-animation-${participant.identity}`;
          videoElement.muted = true;
          videoElement.autoplay = true;
          videoElement.style.display = 'none';
          if (typeof document !== 'undefined') {
            document.body.appendChild(videoElement);
            videoElement.play().catch(() => {});
          }
        }
      },
    );

    // Track unsubscribed
    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (track: any, _publication: any, participant: any) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach().forEach((el: HTMLElement) => el.remove());
          this.audioElements.delete(participant.identity);

          if (this.audioCallbacks) {
            this.audioCallbacks.onAudioLost?.(participant);
          }
        }
      },
    );

    // Connection state changed
    room.on(RoomEvent.ConnectionStateChanged, (state: unknown) => {
      this.setConnectionState(this.mapLiveKitConnectionState(livekit, state));
    });
  }

  async disconnect(): Promise<void> {
    this.cleanup();
    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }
    this.setConnectionState('disconnected');
    globalLiveKitProviderRegistry.delete(this);
  }

  getConnectionState(): string {
    if (!this.room) {
      return 'disconnected';
    }
    return this.connectionState;
  }

  /** @internal */
  async subscribeAnimationTrack(
    callbacks: AnimationTrackCallbacks,
  ): Promise<void> {
    this.animationCallbacks = callbacks;

    // If room is already connected, check for existing tracks
    if (this.room) {
      const livekit = await this.loadSDK();
      const { ConnectionState: LKConnectionState } = livekit;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const roomState = (this.room as any).state;

      if (
        roomState === LKConnectionState.Connected ||
        roomState === 'connected'
      ) {
        // Check for existing video tracks that might be animation tracks
        const remoteParticipants = this.room.remoteParticipants.values();
        for (const participant of remoteParticipants) {
          // Get video track publications (handle different SDK versions)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const pubs =
            (participant as any).videoTrackPublications ??
            (participant as any).trackPublications;
          if (pubs && typeof pubs.values === 'function') {
            for (const publication of pubs.values()) {
              if (
                publication.trackName?.includes('animation') &&
                publication.track
              ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const mediaStreamTrack = (publication.track as any)
                  .mediaStreamTrack as MediaStreamTrack | undefined;
                if (mediaStreamTrack) {
                  const receiver = this.findVideoReceiverByTrackId(
                    mediaStreamTrack.id,
                  );
                  if (receiver) {
                    this.applyAnimationReceiverTransform(
                      receiver,
                      mediaStreamTrack,
                    );
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  /** @internal */
  async unsubscribeAnimationTrack(): Promise<void> {
    this.animationCallbacks = null;

    // Dispose all extractors
    this.vp8Extractors.forEach((extractor) => {
      extractor.dispose();
    });
    this.vp8Extractors.clear();

    if (this.vp8Extractor) {
      this.vp8Extractor.dispose();
      this.vp8Extractor = null;
    }
    this.transformedReceivers.clear();
    this.receiverParticipantMap.clear();
  }

  /** @internal */
  async subscribeAudioTrack(callbacks: AudioTrackCallbacks): Promise<void> {
    this.audioCallbacks = callbacks;
    // Audio tracks are automatically handled in TrackSubscribed event
  }

  /** @internal */
  async unsubscribeAudioTrack(): Promise<void> {
    this.audioCallbacks = null;
    this.audioElements.forEach((el) => {
      el.remove();
    });
    this.audioElements.clear();
  }

  async publishAudioTrack(track?: MediaStreamTrack): Promise<void> {
    if (!this.room) {
      throw new Error('Not connected to room');
    }

    try {
      const livekit = await this.loadSDK();
      const { createLocalAudioTrack, Track: TrackEnum } = livekit;

      // If track is provided, use it; otherwise create from microphone
      if (track) {
        await this.room.localParticipant.publishTrack(track, {
          name: 'user-microphone',
          source: TrackEnum.Source.Microphone,
          red: false,
        });
      } else {
        // Create and publish microphone track
        const localTrack = await createLocalAudioTrack({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });

        this.localAudioTrack = localTrack;

        await this.room.localParticipant.publishTrack(localTrack, {
          name: 'user-microphone',
          source: TrackEnum.Source.Microphone,
          red: false,
        });
      }

      this.isMicrophoneEnabled = true;
    } catch (error) {
      logger.error('LiveKit', 'Failed to publish audio track:', error);
      this.isMicrophoneEnabled = false;
      throw error;
    }
  }

  async unpublishAudioTrack(): Promise<void> {
    if (!this.room || !this.localAudioTrack) {
      return;
    }

    await this.room.localParticipant.unpublishTrack(this.localAudioTrack);
    this.localAudioTrack.stop();
    this.localAudioTrack = null;
    this.isMicrophoneEnabled = false;
  }

  /**
   * Get the native LiveKit Room instance.
   *
   * Allows advanced users to access LiveKit-specific features
   * not exposed through the unified API.
   *
   * @returns The LiveKit Room instance, or null if not connected
   *
   * @example
   * ```typescript
   * const room = provider.getNativeClient();
   * if (room) {
   *   // Access LiveKit-specific features
   *   console.log('Participants:', room.remoteParticipants.size);
   * }
   * ```
   */
  getNativeClient(): LiveKitRoom | null {
    return this.room;
  }

  /**
   * Cleanup resources
   * @internal
   */
  private cleanup(): void {
    this.removeAudioUnlockListeners();

    // Cleanup audio elements
    this.audioElements.forEach((el) => {
      el.remove();
    });
    this.audioElements.clear();

    // Dispose all extractors
    this.vp8Extractors.forEach((extractor) => extractor.dispose());
    this.vp8Extractors.clear();
    this.vp8Extractor = null;

    this.transformedReceivers.clear();
    this.receiverParticipantMap.clear();

    // Stop local audio track
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack = null;
    }
    this.isMicrophoneEnabled = false;
  }
}
